'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import { Sparkles } from 'lucide-react';
import * as React from 'react';
import { useAuth } from '../auth';
import { usePlatform } from '../contexts/platform-context';
import { cn } from '../utils/cn';

import { AccountPanel } from '../components/account-panel';
import { AdminPanel } from '../components/admin-panel';
import { BillingPanel } from '../components/billing-panel';
import { FeatureBlockedModal } from '../components/feature-blocked-modal';
import {
  AccountIcon,
  BillingIcon,
  LinkIcon,
  PersonalizationIcon,
  SettingsIcon,
  TeamIcon,
} from '../components/icons/svg';
import { IntegrationsPanel } from '../components/integrations-panel';
import { ManageTeamPanel } from '../components/manage-team-panel';
import { PerformancePanel } from '../components/performance-panel';
import { PersonalizationPanel } from '../components/personalization-panel';
import { PromptLibraryPanel } from '../components/prompt-library-panel';
import {
  SettingsContent,
  SettingsLayout,
  SettingsNavItem,
  SettingsSidebar,
} from '../components/settings-layout';
import { SystemSettingsPanel } from '../components/system-settings-panel';
import { Activity, LayoutDashboard } from 'lucide-react';

const logger = createLogger('web:view:settings-view');

export const SETTINGS_SECTIONS = [
  'app',
  'account',
  'personalization',
  'prompts',
  'team',
  'billing',
  'integrations',
  'performance',
  'admin',
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/** Type guard for an untrusted string (e.g. a `?panel=` query param). */
export const isSettingsSection = (value: string): value is SettingsSection =>
  (SETTINGS_SECTIONS as readonly string[]).includes(value);

type SettingsViewProps = {
  className?: string;
  onClose?: () => void;
  onGoToChat?: () => void;
  initialSection?: SettingsSection;
  showSidebar?: boolean;
  onNavItemSelect?: () => void;
};

export const SettingsView = ({
  className,
  onClose,
  onGoToChat,
  initialSection = 'account',
  showSidebar = true,
  onNavItemSelect,
}: SettingsViewProps) => {
  const [activeSection, setActiveSection] = React.useState<SettingsSection>(initialSection);
  const { user } = useAuth();
  const { isSidebar } = usePlatform();

  // Modal state for blocked features in sidebar
  const [blockedFeature, setBlockedFeature] = React.useState<{
    name: string;
    path: string;
  } | null>(null);

  const isUserOrganizationAdmin = user?.role === 'admin';

  React.useEffect(() => {
    // For Billing, Integrations, and Admin — require admin
    if (
      !isUserOrganizationAdmin &&
      (activeSection === 'billing' || activeSection === 'integrations' || activeSection === 'admin')
    ) {
      logger.info('User no longer has admin privileges, switching to Account section');
      setActiveSection('account');
    }
    // For Team - just require organization membership
    if (!user?.organizationId && activeSection === 'team') {
      logger.info('User no longer has organization, switching to Account section');
      setActiveSection('account');
    }
  }, [isUserOrganizationAdmin, user?.organizationId, activeSection]);

  const handleClose = () => {
    onClose?.();
  };

  // Scroll-hint state — always declared unconditionally (Rules of Hooks)
  const navRef = React.useRef<HTMLElement>(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  // Always start as true — useEffect immediately hides it if already seen
  const [showHint, setShowHint] = React.useState(true);
  const dismissHint = React.useCallback(() => {
    setShowHint(false);
    localStorage.setItem('settings-tab-scroll-hint-shown', '1');
  }, []);

  React.useEffect(() => {
    if (!isSidebar) return;
    const nav = navRef.current;
    if (!nav) return;
    const check = () => setHasOverflow(nav.scrollWidth > nav.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [isSidebar]);

  React.useEffect(() => {
    if (!isSidebar) return;
    // Hide immediately if already seen (imperceptible — runs after first paint)
    if (localStorage.getItem('settings-tab-scroll-hint-shown')) {
      setShowHint(false);
      return;
    }
    const t = setTimeout(dismissHint, 3500);
    return () => clearTimeout(t);
  }, [isSidebar, dismissHint]);

  const renderContent = () => {
    switch (activeSection) {
      case 'app':
        return (
          <div className="space-y-3">
            <SystemSettingsPanel />
          </div>
        );
      case 'account':
        return (
          <div className="space-y-6 sm:space-y-1">
            <AccountPanel {...(onGoToChat && { onSignOut: onGoToChat })} />
          </div>
        );
      case 'team':
        return (
          <div className="space-y-6 sm:space-y-1">
            <ManageTeamPanel />
          </div>
        );
      case 'billing':
        return (
          <div className="h-full sm:space-y-1">
            <BillingPanel />
          </div>
        );
      case 'integrations':
        return (
          <div className="space-y-6 sm:space-y-1">
            <IntegrationsPanel />
          </div>
        );
      case 'personalization':
        return (
          <div className="space-y-6 sm:space-y-1">
            <PersonalizationPanel />
          </div>
        );
      case 'prompts':
        return (
          <div className="space-y-6 sm:space-y-1">
            <PromptLibraryPanel />
          </div>
        );
      case 'performance':
        return (
          <div className="space-y-6">
            <PerformancePanel />
          </div>
        );
      case 'admin':
        return (
          <div className="space-y-6 sm:space-y-1">
            <AdminPanel />
          </div>
        );
      default:
        return null;
    }
  };

  // Sidebar-specific: horizontal tab strip layout
  if (isSidebar) {
    type TabDef = {
      id: SettingsSection;
      label: string;
      icon: React.ReactNode;
      blocked?: { name: string; path: string };
    };

    const tabs: TabDef[] = [
      { id: 'app', label: 'App', icon: <SettingsIcon className="w-3.5 h-3.5" /> },
      { id: 'account', label: 'Account', icon: <AccountIcon className="w-3.5 h-3.5" /> },
      {
        id: 'personalization',
        label: 'Personal',
        icon: <PersonalizationIcon className="w-3.5 h-3.5" />,
      },
      { id: 'performance', label: 'Perf', icon: <Activity className="w-3.5 h-3.5" /> },
      {
        id: 'prompts',
        label: 'Prompts',
        icon: <Sparkles className="w-3.5 h-3.5" />,
        blocked: { name: 'Prompt Library', path: '/settings?panel=prompts' },
      },
      ...(isUserOrganizationAdmin
        ? [
            {
              id: 'billing' as SettingsSection,
              label: 'Billing',
              icon: <BillingIcon className="w-3.5 h-3.5" />,
              blocked: { name: 'Billing', path: '/settings?panel=billing' },
            },
          ]
        : []),
      ...(user?.organizationId
        ? [
            {
              id: 'team' as SettingsSection,
              label: 'Security & Access',
              icon: <TeamIcon className="w-3.5 h-3.5" />,
            },
          ]
        : []),
      ...(isUserOrganizationAdmin
        ? [
            {
              id: 'integrations' as SettingsSection,
              label: 'Platform',
              icon: <LinkIcon className="w-3.5 h-3.5" />,
            },
          ]
        : []),
      ...(isUserOrganizationAdmin
        ? [
            {
              id: 'admin' as SettingsSection,
              label: 'Admin',
              icon: <LayoutDashboard className="w-3.5 h-3.5" />,
              blocked: { name: 'Admin Dashboard', path: '/settings?panel=admin' },
            },
          ]
        : []),
    ];

    return (
      <SettingsLayout
        onClose={handleClose}
        sidebar
        className={cn('w-full border-border border-[1px] relative', className)}
      >
        {/* Horizontal tab strip */}
        <div className="relative flex-shrink-0">
          <nav
            ref={navRef}
            className="flex items-center gap-1 px-3 py-2 overflow-x-auto scrollbar-none"
            onWheel={e => {
              e.preventDefault();
              e.currentTarget.scrollLeft += e.deltaY;
              dismissHint();
            }}
          >
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  if (tab.blocked) {
                    setBlockedFeature(tab.blocked);
                  } else {
                    setActiveSection(tab.id);
                  }
                  dismissHint();
                  onNavItemSelect?.();
                }}
                className={cn(
                  'flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full',
                  'text-xs font-medium whitespace-nowrap transition-all duration-150',
                  activeSection === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Right-edge fade gradient — visible when tabs overflow */}
          {hasOverflow && (
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-background to-transparent" />
          )}
        </div>

        {/* One-time scroll hint — plain flow banner, zero clipping risk */}
        {showHint && (
          <div className="flex-shrink-0 flex items-center justify-center gap-1.5 py-1 border-b border-border bg-muted/40 animate-in fade-in duration-300">
            <span className="text-[10px] text-muted-foreground">
              ↕ Use scroll wheel on the tabs above
            </span>
          </div>
        )}

        {/* Divider below tab strip */}
        {!showHint && <div className="flex-shrink-0 border-b border-border" />}

        {/* Content */}
        <SettingsContent flat>{renderContent()}</SettingsContent>

        {blockedFeature && (
          <FeatureBlockedModal
            isOpen={true}
            onClose={() => setBlockedFeature(null)}
            featureName={blockedFeature.name}
            targetPath={blockedFeature.path}
          />
        )}
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout
      onClose={handleClose}
      className={cn('w-full border-border border-1 overflow-hidden relative', className)}
    >
      {showSidebar && (
        <SettingsSidebar>
          <nav className="space-y-1">
            <SettingsNavItem
              isActive={activeSection === 'app'}
              onClick={() => {
                setActiveSection('app');
                onNavItemSelect?.();
              }}
            >
              <div className="flex items-center gap-2">
                <SettingsIcon className="w-5 h-5" />
                App
              </div>
            </SettingsNavItem>

            <SettingsNavItem
              isActive={activeSection === 'account'}
              onClick={() => {
                setActiveSection('account');
                onNavItemSelect?.();
              }}
            >
              <div className="flex items-center gap-2">
                <AccountIcon className="w-5 h-5" />
                Account
              </div>
            </SettingsNavItem>

            <SettingsNavItem
              isActive={activeSection === 'personalization'}
              onClick={() => {
                setActiveSection('personalization');
                onNavItemSelect?.();
              }}
            >
              <div className="flex items-center gap-2">
                <PersonalizationIcon className="w-5 h-5" />
                Personalization
              </div>
            </SettingsNavItem>

            <SettingsNavItem
              isActive={activeSection === 'performance'}
              onClick={() => {
                setActiveSection('performance');
                onNavItemSelect?.();
              }}
            >
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Performance
              </div>
            </SettingsNavItem>

            <SettingsNavItem
              isActive={activeSection === 'prompts'}
              onClick={() => {
                setActiveSection('prompts');
                onNavItemSelect?.();
              }}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Prompts
              </div>
            </SettingsNavItem>

            {isUserOrganizationAdmin && (
              <SettingsNavItem
                isActive={activeSection === 'billing'}
                onClick={() => {
                  setActiveSection('billing');
                  onNavItemSelect?.();
                }}
              >
                <div className="flex items-center gap-2">
                  <BillingIcon className="w-5 h-5" />
                  Billing
                </div>
              </SettingsNavItem>
            )}

            {user?.organizationId && (
              <SettingsNavItem
                isActive={activeSection === 'team'}
                onClick={() => {
                  setActiveSection('team');
                  onNavItemSelect?.();
                }}
              >
                <div className="flex items-center gap-2">
                  <TeamIcon className="w-5 h-5" />
                  Security & Access
                </div>
              </SettingsNavItem>
            )}

            {isUserOrganizationAdmin && (
              <SettingsNavItem
                isActive={activeSection === 'integrations'}
                onClick={() => {
                  setActiveSection('integrations');
                  onNavItemSelect?.();
                }}
              >
                <div className="flex items-center gap-2">
                  <LinkIcon className="w-5 h-5" />
                  Platform
                </div>
              </SettingsNavItem>
            )}

            {isUserOrganizationAdmin && (
              <SettingsNavItem
                isActive={activeSection === 'admin'}
                onClick={() => {
                  setActiveSection('admin');
                  onNavItemSelect?.();
                }}
              >
                <div className="flex items-center gap-2">
                  <LayoutDashboard className="w-5 h-5" />
                  Admin
                </div>
              </SettingsNavItem>
            )}
          </nav>
        </SettingsSidebar>
      )}

      <SettingsContent>{renderContent()}</SettingsContent>

      {/* Feature blocked modal for sidebar */}
      {blockedFeature && (
        <FeatureBlockedModal
          isOpen={true}
          onClose={() => setBlockedFeature(null)}
          featureName={blockedFeature.name}
          targetPath={blockedFeature.path}
        />
      )}
    </SettingsLayout>
  );
};

SettingsView.displayName = 'SettingsView';
